/**
 * `05/ Sign Offs` — the sign-off QUEUE at `/countries/:cc/signoffs`.
 *
 * ===========================================================================
 * THIS IS A CHASE LIST. IT SIGNS NOTHING.
 * ===========================================================================
 * The route is gated on `crf.view`, not on `signoff.developer`/`signoff.project`,
 * and the controller says why: chasing is not signing. A Coordinator whose whole
 * job is finding out who has not signed holds neither write key, and gating the
 * READ on them would hide the chase list from the person doing the chasing — the
 * Work Board defect, exactly.
 *
 * So this screen draws NO sign, approve, remind or send control. Not disabled
 * ones — none. A control the caller's permissions cannot back would advertise a
 * capability they do not have; a control this screen COULD back would make a
 * read-gated register into a signing surface, which is the reason the gate was
 * argued down to `crf.view` in the first place. The row's one action is a link to
 * the CRF record, where signing already lives behind its own two keys.
 *
 * ===========================================================================
 * THE MAIL COLUMN IS THE DEVELOPER ROW'S MAIL, AND THE HEADING SAYS SO
 * ===========================================================================
 * `developer_mail_status` reads the DEVELOPER sign-off row and only that row. The
 * service shipped this wrong once as a bare "whichever signature we are waiting
 * on" column, which was structurally NULL in both branches — a permanently blank
 * "Mail" heading, which in a UI is worse than no column because it implies mail
 * tracking that is not happening. The heading here is therefore DEVELOPER MAIL,
 * never "Mail".
 *
 * Two consequences this screen holds to:
 *
 *   · On the DEVELOPER queue the value is null, because no developer sign-off row
 *     exists yet — its absence is what put the CRF in that queue. That renders as
 *     "No record". NEVER "Not sent". This system sends no sign-off mail at all;
 *     `mail_status` is a fact an operator writes down about a message that
 *     reached THEM. A negative delivery state would describe a transport that
 *     does not exist.
 *   · There is deliberately NO column for the PROJECT signature's mail. The row
 *     it would read does not exist — it is the row every one of these CRFs is
 *     waiting for.
 *
 * ===========================================================================
 * NO OVERDUE TINT ON `days_waiting`, AND THAT IS A REFUSAL
 * ===========================================================================
 * `DaysPill` exists and would look right here, but it carries
 * `OVERDUE_DAYS_IN_STAGE = 7` and the title "Nd in stage" — a threshold and a
 * clock that belong to an INQUIRY's time in a pipeline stage, not to a signature
 * that has not arrived. Nothing in the spec, the API or the design names a number
 * of days after which a sign-off is late. Painting one red would be this screen
 * inventing a claim the server never made, so the days render plain and the
 * ordering (longest wait first, the server's) carries the urgency instead.
 *
 * `days_waiting` is also NULLABLE — a CRF marked deployed with no deployment row
 * starts no clock. Null renders as a sentence saying the clock has no start. "0
 * days" would be a different and false claim: that the wait began today. Those
 * rows sort to the BOTTOM: the service orders on `waiting_since IS NULL ASC`
 * before the timestamp, so a wait nobody has measured cannot claim a place in a
 * longest-first list. They are still listed — an outstanding signature is still
 * someone's job — and their clock cell says the start is unrecorded.
 *
 * ⚠ THIS PARAGRAPH WAS WRONG FOR ONE COMMIT. It read "those rows also lead the
 * table … because the service orders on `waiting_since ASC` and MySQL sorts NULLs
 * first", which described the API accurately at the time. The API's ordering was
 * then corrected — NULLs leading a chase list put the least informative row where
 * the operator looks first — and this file was not updated with it. A screen whose
 * accessible text describes the previous release is exactly as wrong as one that
 * describes nothing, and it is harder to notice.
 *
 * ===========================================================================
 * THIS IS NOT EVERY UNSIGNED CRF, AND THE SCREEN SAYS SO
 * ===========================================================================
 * The service's WHERE is `c.status = 'deployed'` — deploying is what puts a CRF in
 * front of a signer. A CRF still in development is unsigned too and is correctly
 * absent, because nobody is waiting on it. The framing line under the header makes
 * that boundary explicit instead of letting "who has not signed" be read as a
 * complete list, which it is not and was never meant to be.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { relativeTime } from '../../domain/events';
import { formatDateLong } from '../inquiries/format';
import { cn } from '../../lib/cn';
import { WorkTypeBadge } from '../crfs/WorkTypeBadge';
import { deploymentKeys, fetchSignoffQueue, registerErrorText } from './deployment-api';
import type { SignoffQueueFilters, SignoffQueueRow } from './deployment-api';

const PER_PAGE = 50;

/**
 * The filter chips, in the service's own order with "All" in front. `value: null`
 * is the absence of the parameter, not a third queue.
 */
const QUEUE_FILTERS: readonly { value: string | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'developer', label: 'Developer' },
  /*
   * ⚠ "Countersignature", NOT "Project" — AND THE `value` IS UNTOUCHED (design D-8,
   * Projects C8). `signoffs.type = 'project'` predates migration 37 and means "the second
   * signature, the one that closes the record". This screen now ALSO contains
   * `work_type='project'` records, so the single word "Project" carries two senses at
   * once on one screen — and worse, they compose: `?queue=project&work_type=project` is
   * a meaningful pair. A chip that could mean either is a filter an operator cannot
   * predict, so the LABEL names the act.
   *
   * The wire value, the query parameter and `SIGNOFF_QUEUES` all stay `'project'`: the
   * ambiguity is in the copy, and renaming a shipped identifier to fix copy would break
   * every saved URL for no gain.
   */
  { value: 'project', label: 'Countersignature' },
];

/**
 * Which signature is outstanding. Both pairs already exist in the app's stage
 * vocabulary and NEITHER is a status tone — a queue is not good or bad news, it
 * is a destination. Keyed by `string` so an unrecognised queue renders the
 * server's own word on a neutral chip rather than a blank pill.
 */
const QUEUE_PILL: Readonly<Record<string, { label: string; classes: string }>> = {
  developer: { label: 'Developer', classes: 'bg-info-bg text-info-fg' },
  // The ROW pill takes the same word as the filter chip above, or a reader who filtered by
  // "Countersignature" would find a column headed "Project" and reasonably conclude the
  // filter had done something else.
  project: { label: 'Countersignature', classes: 'bg-stage-confirm-bg text-stage-confirm-fg' },
};

function queuePill(queue: string): { label: string; classes: string } {
  return QUEUE_PILL[queue] ?? { label: queue, classes: 'bg-subtle text-fg ring-1 ring-line' };
}

/**
 * `signoffs.mail_status` → what an operator reads. Mirrors the labels
 * `SignoffPanel` already renders on the work-order record, so the same column
 * reads the same everywhere; that map is file-private there, which is why this is
 * a second copy rather than an import (flagged in the Block C report).
 *
 * `null` is NOT in this map. It is not a mail state — it is the absence of the
 * row that would carry one, and it is handled separately below so the two can
 * never be conflated.
 */
const MAIL_LABEL: Readonly<Record<string, string>> = {
  none: 'No mail recorded',
  sent: 'Mail sent',
  received: 'Mail received',
  acknowledged: 'Acknowledged',
};

export function SignoffQueuePage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'crf.view');

  const rawQueue = params.get('queue');
  const queue = rawQueue === null || rawQueue === '' ? null : rawQueue;
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters = useMemo<SignoffQueueFilters>(
    () => ({ queue: queue ?? undefined, page, perPage: PER_PAGE }),
    [queue, page],
  );

  const list = useQuery({
    queryKey: deploymentKeys.signoffs(countryCode, filters),
    queryFn: ({ signal }) => fetchSignoffQueue(countryCode, filters, signal),
    enabled: canView,
  });

  if (!canView) {
    // D5 — a designed refusal that names who to ask and never the permission key.
    // Never an empty table, which would read as "nothing is waiting" — the most
    // expensive wrong answer a chase list can give.
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Sign offs are not available to this account">
          Viewing the sign-off queue in {countryCode} needs the CRF permission. Ask an
          administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;

  const setQueue = (next: string | null): void => {
    const patch = new URLSearchParams(params);
    if (next === null) patch.delete('queue');
    else patch.set('queue', next);
    // A filter change resets paging — page 3 of the developer queue is not page 3
    // of both queues.
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Sign Offs"
        subtitle={
          list.data === undefined
            ? undefined
            : // The SERVER'S total, never the length of this page. One row is
              // exactly one outstanding signature, so the unit is signatures.
              `${String(list.data.total)} ${
                list.data.total === 1 ? 'signature outstanding' : 'signatures outstanding'
              }`
        }
      />

      {/*
       * The screen's own boundary, said out loud rather than left to be inferred
       * from the absence of buttons.
       */}
      <p className="text-body13 text-fg-secondary">
        Which deployed CRFs are still waiting for a signature, and for how long. This is not
        every unsigned CRF — a CRF that has not been deployed is not waiting on anybody.
        Nothing is signed from here: open the CRF to record a signature. No mail is sent by
        this system; the mail column is what the developer sign-off recorded.
      </p>

      <div role="group" aria-label="Filter by queue" className="flex flex-wrap items-center gap-2">
        {QUEUE_FILTERS.map((chip) => (
          <QueueChip
            key={chip.label}
            label={chip.label}
            active={queue === chip.value}
            onSelect={() => setQueue(chip.value)}
          />
        ))}
      </div>

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the sign-off queue" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The sign-off queue could not be loaded">
          {/* The server's own sentence — on a 400 it names the vocabulary. */}
          <p>{registerErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        // An empty PAGE is not an empty RESULT SET — the quotes-list V3 lesson.
        <Alert tone="info" title={`Page ${String(list.data.page)} is past the end of this register`}>
          <p>
            The register holds {String(total)}{' '}
            {total === 1 ? 'outstanding signature' : 'outstanding signatures'}.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        queue === null ? (
          <div className="rounded-md border border-dashed border-line bg-surface">
            <EmptyState title="No CRF in this country is waiting for a signature." />
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-line bg-surface">
            <EmptyState
              title="No CRF in this country is waiting for that signature."
              action={
                <button
                  type="button"
                  onClick={() => setQueue(null)}
                  className="text-body13 font-medium text-indigo hover:underline"
                >
                  Show both queues.
                </button>
              }
            />
          </div>
        )
      ) : (
        <SignoffQueueTable countryCode={countryCode} rows={rows} />
      )}

      {/* Every paging number below comes off the one payload the rows came off. */}
      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── chips ── */

/** The Toolbar filter-chip geometry (6:467). No count: this route serves none. */
function QueueChip({
  label,
  active,
  onSelect,
}: {
  label: string;
  active: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5 text-body13',
        active ? 'border-line-strong font-medium text-fg' : 'text-fg-secondary hover:text-fg',
      )}
    >
      {label}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────── table ── */

function SignoffQueueTable({
  countryCode,
  rows,
}: {
  countryCode: string;
  rows: readonly SignoffQueueRow[];
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1220px] border-collapse text-left">
        <caption className="sr-only">
          Every CRF in {countryCode} waiting for a signature, longest wait first
        </caption>
        <colgroup>
          <col className="w-[170px]" />
          <col />
          <col className="w-[130px]" />
          <col className="w-[190px]" />
          <col className="w-[200px]" />
          <col className="w-[160px]" />
          <col className="w-[110px]" />
        </colgroup>

        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              CRF
            </th>
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              CUSTOMER
            </th>
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              WAITING FOR
            </th>
            {/*
             * Server-ordered by `waiting_since IS NULL ASC, waiting_since ASC` —
             * stated, not a dead sort control. The leading IS NULL key is what
             * keeps a row with no clock at the BOTTOM; without it MySQL would sort
             * NULLs first and the top of a chase list would be the row carrying no
             * information. The sr-only text below must keep saying what the server
             * actually does — the ↓ glyph is aria-hidden, so this sentence is the
             * ONLY ordering information a screen-reader user gets.
             */}
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              WAITING <span aria-hidden="true">↓</span>
              <span className="sr-only">
                , longest wait first; rows with no recorded start come last
              </span>
            </th>
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              DEVELOPER SIGN-OFF
            </th>
            {/*
             * "DEVELOPER MAIL", never "Mail": this reads the DEVELOPER sign-off
             * row and no other. There is no project-mail column because the row
             * it would read is the one every CRF here is waiting for.
             */}
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              DEVELOPER MAIL
            </th>
            <th scope="col" className="px-3">
              <span className="sr-only">Row actions</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const pill = queuePill(row.queue);
            return (
              <tr key={row.crf_id} className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle">
                <td className="px-3 py-3 align-middle">
                  {/*
                    ⚠ THE BADGE IS WHY THE QUEUE CHIP HAD TO BE RELABELLED. On this one
                    screen "project" now names two different things at once — the
                    outstanding COUNTERSIGNATURE and the kind of RECORD — and they compose
                    (`?queue=project&work_type=project` is a meaningful pair). The queue
                    keeps its identifier and loses the word; the record keeps the word.
                  */}
                  <span className="flex items-center gap-2">
                    <Link
                      to={`/countries/${countryCode}/crfs/${row.crf_id}`}
                      className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
                    >
                      {row.crf_doc_no}
                    </Link>
                    <WorkTypeBadge workType={row.work_type} />
                  </span>
                </td>

                <td className="px-3 py-3 align-middle text-body13 text-fg">{row.party_name}</td>

                <td className="px-3 py-3 align-middle">
                  <span
                    // Addressed by QUEUE, never by row index.
                    data-queue={row.queue}
                    className={cn(
                      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
                      pill.classes,
                    )}
                  >
                    <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
                    {pill.label}
                  </span>
                </td>

                <WaitingCell days={row.days_waiting} since={row.waiting_since} />

                <DeveloperSignoffCell at={row.developer_signed_at} by={row.developer_signed_by} />

                <DeveloperMailCell status={row.developer_mail_status} />

                <td className="px-3 py-3 align-middle">
                  {/*
                   * The row's ONE action, and it is navigation. This is the cell a
                   * "Sign" button would have occupied; it holds a link instead,
                   * and that is the whole difference between a chase list and a
                   * signing surface.
                   */}
                  <Link
                    to={`/countries/${countryCode}/crfs/${row.crf_id}`}
                    className="whitespace-nowrap text-body13 font-medium text-indigo hover:underline"
                  >
                    Open CRF
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * How long this signature has been outstanding.
 *
 * `days_waiting` is the SERVER's arithmetic, rendered rather than recomputed, so
 * every reader of this row agrees on the number. Null means no clock was ever
 * started and is said in words — "0 days" would claim the wait began today. No
 * overdue tint: see the file header.
 */
function WaitingCell({
  days,
  since,
}: {
  days: number | null;
  since: string | null;
}): React.JSX.Element {
  if (days === null) {
    return (
      <td className="px-3 py-3 align-middle">
        <span
          data-waiting="no-clock"
          title="This CRF is marked deployed but has no deployment recorded, so nothing started the clock."
          className="text-label text-fg-muted"
        >
          No start recorded
        </span>
      </td>
    );
  }
  return (
    <td className="px-3 py-3 align-middle">
      <div className="flex flex-col gap-0.5">
        <span className="tnum text-body13 font-medium text-fg">
          {String(days)} {days === 1 ? 'day' : 'days'}
        </span>
        {since === null ? null : (
          <span className="text-label text-fg-muted">since {formatDateLong(since)}</span>
        )}
      </div>
    </td>
  );
}

/**
 * The developer signature — the project queue's precondition, and on the
 * developer queue the very thing being waited for.
 *
 * `Not signed` is the wording `SignoffPanel`'s own `SignoffRow` uses on the work
 * order, kept so the same fact reads the same in both places.
 */
function DeveloperSignoffCell({
  at,
  by,
}: {
  at: string | null;
  by: string | null;
}): React.JSX.Element {
  if (at === null) {
    return (
      <td className="px-3 py-3 align-middle">
        <span className="text-label text-fg-muted">Not signed</span>
      </td>
    );
  }
  return (
    <td className="px-3 py-3 align-middle">
      <div className="flex flex-col gap-0.5">
        <span className="text-body13 text-fg">
          {formatDateLong(at)}
          <span className="text-fg-muted"> · {relativeTime(at)}</span>
        </span>
        <span className="text-label text-fg-muted">
          {by === null ? (
            <span title="The account that signed no longer exists, so it cannot be named.">
              Signed by an account that no longer exists
            </span>
          ) : (
            `by ${by}`
          )}
        </span>
      </div>
    </td>
  );
}

/**
 * What the DEVELOPER sign-off row recorded about mail. See the file header for
 * why null is "No record" and never "Not sent", and why there is no second column
 * for the project signature.
 */
function DeveloperMailCell({ status }: { status: string | null }): React.JSX.Element {
  if (status === null) {
    return (
      <td className="px-3 py-3 align-middle">
        <span
          data-mail="no-record"
          title="No developer sign-off row exists yet, so there is nothing to report. This system sends no sign-off mail."
          className="inline-flex items-center rounded-pill bg-subtle px-2.5 py-1 text-label text-fg-muted ring-1 ring-line"
        >
          No record
        </span>
      </td>
    );
  }
  return (
    <td className="px-3 py-3 align-middle">
      <span data-mail={status} className="text-body13 text-fg-secondary">
        {/* An unrecognised value keeps the server's own word. */}
        {MAIL_LABEL[status] ?? status}
      </span>
    </td>
  );
}

/* ────────────────────────────────────────────────────────────────── pager ── */

/**
 * `Table/Pagination` — re-authored per screen, the house pattern every list in
 * this app follows: the geometry is shared, the component is file-private.
 */
function PagerFooter({
  page,
  perPage,
  shown,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  shown: number;
  total: number;
  onPage: (page: number) => void;
}): React.JSX.Element {
  const firstOnPage = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastOnPage = (page - 1) * perPage + shown;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-2">
      <p className="text-body13 text-fg-secondary">{perPage} per page</p>
      <div className="flex items-center gap-2">
        <p className="tnum text-body13 text-fg-secondary">
          {firstOnPage}–{lastOnPage} of {total}
        </p>
        <PageButton label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden="true" className="size-4" />
        </PageButton>
        <PageButton
          label="Next page"
          disabled={lastOnPage >= total}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
